import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const lines = (v) => (Array.isArray(v) ? v : String(v || "").split("\n").map((x) => x.trim()).filter(Boolean));

function F({ label, children }) {
  return <div><label className="jt-eyebrow mb-2 block">{label}</label>{children}</div>;
}

function useList(url) {
  const [list, setList] = useState([]);
  const load = () => api.get(url).then((r) => setList(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [list, load];
}

function Rows({ items, onEdit, onDelete, render, prefix }) {
  return (
    <div className="bg-white rounded-xl ring-1 ring-black/5 divide-y">
      {items.map((x) => (
        <div key={x.id} className="p-4 flex items-center gap-4" data-testid={`${prefix}-row-${x.slug}`}>
          <img src={x.image_url} alt="" className="w-20 h-14 rounded-md object-cover bg-[#F5F0EB]"/>
          <div className="flex-1">{render(x)}{!x.is_active && <span className="text-xs text-[#C86D51]"> (inactive)</span>}</div>
          <button onClick={() => onEdit(x)} className="p-2" data-testid={`${prefix}-edit-${x.slug}`}><Pencil size={16}/></button>
          <button onClick={() => onDelete(x)} className="p-2 text-[#C86D51]" data-testid={`${prefix}-del-${x.slug}`}><Trash2 size={16}/></button>
        </div>
      ))}
    </div>
  );
}

async function deactivate(url, reload) {
  if (!window.confirm("Deactivate this item? It will be hidden from the website.")) return;
  try { await api.delete(url); toast.success("Deactivated"); reload(); } catch (e) { toast.error(formatErr(e)); }
}

const BLANK_ROOM = { name: "", description: "", max_guests: 2, price_per_night: 5000, total_rooms: 1 };
const BLANK_HOTEL = {
  name: "", property_type: "luxury_tent", short_description: "", description: "", location: "Jawai, Pali, Rajasthan",
  amenities: [], image_url: "", gallery: [], check_in_time: "13:00", check_out_time: "11:00", rating: 4.5,
  room_types: [{ ...BLANK_ROOM }], is_active: true,
};

export function HotelManager() {
  const [list, load] = useList("/admin/hotels");
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setRoom = (i, k, v) => set("room_types", form.room_types.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    const payload = {
      ...form, rating: Number(form.rating), amenities: lines(form.amenities), gallery: lines(form.gallery),
      room_types: form.room_types.map((r) => ({ ...r, max_guests: Number(r.max_guests), price_per_night: Number(r.price_per_night), total_rooms: Number(r.total_rooms) })),
    };
    try {
      if (form.id) await api.patch(`/admin/hotels/${form.id}`, payload);
      else await api.post("/admin/hotels", payload);
      toast.success("Stay saved"); setForm(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-xl text-[#1E3A2B]">Hotels & stays</h2>
        <button onClick={() => setForm(BLANK_HOTEL)} className="jt-pill-accent text-sm" data-testid="admin-new-hotel"><Plus size={14}/>New stay</button>
      </div>
      <Rows items={list} prefix="hotel" onEdit={(h) => setForm(h)} onDelete={(h) => deactivate(`/admin/hotels/${h.id}`, load)}
        render={(h) => (<><div className="font-medium inline">{h.name}</div>
          <div className="text-xs text-[#5C6B63]">{h.property_type} · {h.room_types.map((r) => `${r.name} (${r.total_rooms} × ${inr(r.price_per_night)})`).join(", ")}</div></>)}/>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        {form && (
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Edit stay" : "New stay"}</DialogTitle></DialogHeader>
            <div className="grid gap-4 md:grid-cols-2">
              <F label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="h-name"/></F>
              <F label="Type">
                <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="h-type"><SelectValue/></SelectTrigger>
                  <SelectContent>{["luxury_tent", "cottage", "resort", "heritage", "homestay"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)}/></F>
              <F label="Rating"><Input type="number" step="0.1" value={form.rating} onChange={(e) => set("rating", e.target.value)}/></F>
              <F label="Check-in time"><Input value={form.check_in_time} onChange={(e) => set("check_in_time", e.target.value)}/></F>
              <F label="Check-out time"><Input value={form.check_out_time} onChange={(e) => set("check_out_time", e.target.value)}/></F>
              <div className="md:col-span-2"><F label="Cover image URL"><Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="h-img"/></F></div>
              <div className="md:col-span-2"><F label="Short description"><Textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} data-testid="h-short"/></F></div>
              <div className="md:col-span-2"><F label="Full description"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}/></F></div>
              <F label="Amenities (one per line)"><Textarea rows={4} value={lines(form.amenities).join("\n")} onChange={(e) => set("amenities", e.target.value)}/></F>
              <F label="Gallery URLs (one per line)"><Textarea rows={4} value={lines(form.gallery).join("\n")} onChange={(e) => set("gallery", e.target.value)}/></F>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="jt-eyebrow">Room types & inventory</span>
                <button onClick={() => set("room_types", [...form.room_types, { ...BLANK_ROOM }])} className="text-sm text-[#C86D51]" data-testid="h-add-room">+ Add room type</button>
              </div>
              <div className="space-y-2">
                {form.room_types.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end bg-[#F5F0EB] p-3 rounded-lg" data-testid={`h-room-${i}`}>
                    <div className="col-span-12 md:col-span-3"><F label="Name"><Input value={r.name} onChange={(e) => setRoom(i, "name", e.target.value)} data-testid={`h-room-name-${i}`}/></F></div>
                    <div className="col-span-12 md:col-span-3"><F label="Description"><Input value={r.description} onChange={(e) => setRoom(i, "description", e.target.value)}/></F></div>
                    <div className="col-span-4 md:col-span-2"><F label="₹/night"><Input type="number" value={r.price_per_night} onChange={(e) => setRoom(i, "price_per_night", e.target.value)} data-testid={`h-room-price-${i}`}/></F></div>
                    <div className="col-span-3 md:col-span-1"><F label="Guests"><Input type="number" value={r.max_guests} onChange={(e) => setRoom(i, "max_guests", e.target.value)}/></F></div>
                    <div className="col-span-3 md:col-span-2"><F label="Rooms"><Input type="number" value={r.total_rooms} onChange={(e) => setRoom(i, "total_rooms", e.target.value)} data-testid={`h-room-count-${i}`}/></F></div>
                    <button className="col-span-2 md:col-span-1 p-2 text-[#C86D51]" onClick={() => set("room_types", form.room_types.filter((_, j) => j !== i))} aria-label="Remove room"><X size={16}/></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)}/><span className="text-sm">Active (visible on site)</span>
            </div>
            <DialogFooter><button onClick={save} className="jt-pill-primary" data-testid="h-save">Save stay</button></DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

const BLANK_PKG = { name: "", short_description: "", description: "", hotel_id: "", room_type_id: "", safari_id: "", nights: 2, price: 20000, inclusions: [], image_url: "", is_active: true };

export function PackageManager() {
  const [list, load] = useList("/admin/packages");
  const [hotels] = useList("/admin/hotels");
  const [safaris] = useList("/admin/safaris");
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const hotel = hotels.find((h) => h.id === form?.hotel_id);

  const save = async () => {
    try {
      const payload = { ...form, nights: Number(form.nights), price: Number(form.price), inclusions: lines(form.inclusions) };
      if (form.id) await api.patch(`/admin/packages/${form.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Package saved"); setForm(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-xl text-[#1E3A2B]">Stay + safari packages</h2>
        <button onClick={() => setForm(BLANK_PKG)} className="jt-pill-accent text-sm" data-testid="admin-new-package"><Plus size={14}/>New package</button>
      </div>
      <Rows items={list} prefix="pkg" onEdit={(p) => setForm(p)} onDelete={(p) => deactivate(`/admin/packages/${p.id}`, load)}
        render={(p) => (<><div className="font-medium inline">{p.name}</div>
          <div className="text-xs text-[#5C6B63]">{p.nights} nights · {inr(p.price)} · {hotels.find((h) => h.id === p.hotel_id)?.name}</div></>)}/>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        {form && (
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2"><F label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="p-name"/></F></div>
              <F label="Stay">
                <Select value={form.hotel_id} onValueChange={(v) => setForm({ ...form, hotel_id: v, room_type_id: "" })}>
                  <SelectTrigger data-testid="p-hotel"><SelectValue placeholder="Choose stay"/></SelectTrigger>
                  <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Room type">
                <Select value={form.room_type_id} onValueChange={(v) => set("room_type_id", v)}>
                  <SelectTrigger data-testid="p-room"><SelectValue placeholder="Choose room"/></SelectTrigger>
                  <SelectContent>{(hotel?.room_types || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Safari (1 private jeep)">
                <Select value={form.safari_id} onValueChange={(v) => set("safari_id", v)}>
                  <SelectTrigger data-testid="p-safari"><SelectValue placeholder="Choose safari"/></SelectTrigger>
                  <SelectContent>{safaris.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Nights"><Input type="number" value={form.nights} onChange={(e) => set("nights", e.target.value)} data-testid="p-nights"/></F>
                <F label="Price (₹)"><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="p-price"/></F>
              </div>
              <div className="md:col-span-2"><F label="Image URL (blank = stay photo)"><Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)}/></F></div>
              <div className="md:col-span-2"><F label="Short description"><Textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} data-testid="p-short"/></F></div>
              <div className="md:col-span-2"><F label="Full description"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}/></F></div>
              <div className="md:col-span-2"><F label="Inclusions (one per line)"><Textarea rows={4} value={lines(form.inclusions).join("\n")} onChange={(e) => set("inclusions", e.target.value)}/></F></div>
              <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)}/><span className="text-sm">Active</span></div>
            </div>
            <DialogFooter><button onClick={save} className="jt-pill-primary" data-testid="p-save">Save package</button></DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
